<script lang="ts">
import type { InputHTMLAttributes } from 'vue';
import type { UseIconsProps } from 'composables/useIcons';
import type { AcceptableValue } from 'types/utils';
import type { Colors, Variants, Sizes } from 'types';

interface ModelModifiers {
  string?: boolean;
  number?: boolean;
  trim?: boolean;
  lazy?: boolean;
  nullify?: boolean;
}

export interface InputProps extends UseIconsProps {
  as?: string | object;
  id?: string;
  name?: string;
  type?: InputHTMLAttributes['type'];
  /** Display an icon based on the `leading` and `trailing` props. */
  icon?: string;
  /** When `true`, the icon will be displayed on the left side. */
  leading?: boolean;
  /** Display an icon on the left side. */
  leadingIcon?: string;
  /** When `true`, the icon will be displayed on the right side. */
  trailing?: boolean;
  /** Display an icon on the right side. */
  trailingIcon?: string;
  /** When `true`, the loading icon will be displayed. */
  loading?: boolean;
  /**
   * The icon when the `loading` prop is `true`.
   * @defaultValue 'reload'
   */
  loadingIcon?: string;
  /**
   * The placeholder text when the select is empty.
   */
  placeholder?: string;
  /**
   * The color of the input.
   * @defaultValue 'neutral'
   */
  color?: Colors;
  /**
   * The variant of the input.
   */
  variant?: Variants;
  /**
   * The size of the input.
   */
  size?: Sizes;
  required?: boolean;
  autocomplete?: InputHTMLAttributes['autocomplete'];
  autofocus?: boolean;
  autofocusDelay?: number;
  disabled?: boolean;
  modelModifiers?: ModelModifiers;
  class?: string;
  modelValue?: AcceptableValue;
}

export interface InputEmits {
  (e: 'update:modelValue', value: AcceptableValue): void;
  (e: 'blur' | 'change', value: boolean): void;
}

export interface InputSlots {
  leading(props?: {}): unknown;
  default(props?: {}): unknown;
  trailing(props?: {}): unknown;
  [key: string]: (props?: {}) => unknown;
}
</script>

<script setup lang="ts">
import { ref, computed, onMounted, toRef } from 'vue';
import { Primitive } from 'reka-ui';
import { looseToNumber } from 'utils';
import { useIcons } from 'composables/useIcons';
import { useConditionalSlots } from 'composables/useConditionalSlots';
import { useFormAssociated } from 'composables/useFormAssociated';

defineOptions({ inheritAttrs: false });

// eslint-disable-next-line vue/no-dupe-keys
const [modelValue, modelModifiers] = defineModel<AcceptableValue>();

const props = withDefaults(defineProps<InputProps>(), {
  type: 'text',
  autocomplete: 'off',
  autofocusDelay: 0,
  color: 'neutral',
  variant: 'soft',
  size: 'md'
});
const emit = defineEmits<InputEmits>();
defineSlots<InputSlots>();

const { isLeading, isTrailing, leadingIconName, trailingIconName } = useIcons(props);

const elRef = ref<HTMLElement | null>(null);
const inputRef = ref<HTMLInputElement | null>(null);
// wire it up
const { validate } = useFormAssociated({
  inputRef,
  modelValue,
  disabled: toRef(props, 'disabled'),
  required: toRef(props, 'required')
  // optional custom serialization:
  // toFormValue: (v) => v == null ? null : JSON.stringify(v)
});

const { leading: hasLeading, trailing: hasTrailing } = useConditionalSlots<InputSlots>(elRef, [
  'leading',
  'trailing'
]);

const updateInput = (value: string) => {
  let processedValue: AcceptableValue = value;

  if (modelModifiers.trim && typeof processedValue === 'string') {
    processedValue = processedValue.trim() || null;
  }

  if (modelModifiers.number || props.type === 'number') {
    processedValue = looseToNumber(processedValue);
  }

  if (modelModifiers.nullify) {
    processedValue = processedValue || null;
  }

  modelValue.value = processedValue;
};

const onInput = (e: Event) => {
  const value = (e.target as HTMLInputElement).value;
  if (!modelModifiers.lazy) {
    updateInput(value);
    validate();
  }
};

const onChange = (e: Event) => {
  const value = (e.target as HTMLInputElement).value;

  if (modelModifiers.lazy) {
    updateInput(value);
  }

  if (modelModifiers.trim) {
    (e.target as HTMLInputElement).value = value.trim();
  }

  validate();
  emit('change', true);
};

const onBlur = () => {
  validate();
  emit('blur', true);
};

const autoFocus = () => {
  if (props.autofocus) {
    inputRef.value?.focus();
  }
};

onMounted(() => {
  setTimeout(() => {
    autoFocus();
  }, props.autofocusDelay);
});

const classes = computed(() =>
  [
    'e-input',
    props.color && `e-input-color-${props.color}`,
    props.variant && `e-input-variant-${props.variant}`,
    props.size && `e-input-size-${props.size}`
  ]
    .filter(Boolean)
    .join(' ')
);

defineExpose({
  inputRef
});
</script>

<template>
  <primitive
    :as="props.as"
    :class="classes"
  >
    <input
      :id="id"
      ref="inputRef"
      :type="type"
      :value="modelValue"
      :name="name"
      :placeholder="placeholder"
      :disabled="disabled"
      :required="required"
      :autocomplete="autocomplete"
      v-bind="{ ...$attrs }"
      @input="onInput"
      @blur="onBlur"
      @change="onChange"
    />

    <slot />

    <span
      v-if="isLeading || hasLeading"
      class="e-input-icon e-input-leading"
    >
      <slot name="leading">
        <e-icon
          v-if="isLeading && leadingIconName"
          :name="leadingIconName"
        />
      </slot>
    </span>

    <span
      v-if="isTrailing || hasTrailing"
      class="e-input-icon e-input-trailing"
    >
      <slot name="trailing">
        <e-icon
          v-if="isTrailing && trailingIconName"
          :name="trailingIconName"
        />
      </slot>
    </span>
  </primitive>
</template>
